import { CompiledChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import Highcharts from 'highcharts'
import _ from 'lodash'
import { DateTime } from 'luxon'
import type DimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/DimensionResolver'

export interface SeriesDimensions {
  [key: string]: unknown
}

export interface SegmentBound {
  start: number
  end: number
  includesDSTShift: boolean
  actualLength: number
  alreadyDone: boolean
  notYetStarted: boolean
}

export interface DataPoint {
  start: number
  end: number
  x: number
  y: number
}

export interface SeriesData<T> {
  dimensions: SeriesDimensions
  entries: T[]
  data?: DataPoint[]
  name?: string
}

export abstract class DatabaseEntryChartResultPipeline<T = unknown> {
  protected readonly DAY_START = 'day' as const
  protected readonly MILLISECOND_UNIT = 'millisecond' as const
  protected readonly HOURS_UNIT = 'hours' as const
  protected readonly MINUTES_UNIT = 'minutes' as const
  protected resolverLookup: { [key: string]: DimensionResolver } = {}

  /**
   * Get the supported resolver classes for this pipeline
   */
  protected abstract getSupportedResolverClasses(): (typeof DimensionResolver)[]

  /**
   * Extract timestamp from an entry based on the timestamp property
   */
  protected abstract getTimestampFromEntry(entry: T, timestampProperty: string): number | null

  /**
   * Determine if an entry should be included based on additional criteria
   * Default implementation just checks timestamp bounds
   */
  protected shouldIncludeEntry(entry: T, bound: SegmentBound, timestampProperty: string): boolean {
    const timestamp = this.getTimestampFromEntry(entry, timestampProperty)
    if (timestamp === null) return false
    return this.isTimestampInBounds(timestamp, bound)
  }

  async prepareResolvers(tagKeys: string[], firstDataPoint: T) {
    const supportedResolverClasses = this.getSupportedResolverClasses()
    const relevantResolverClasses = supportedResolverClasses.filter(resolverClass => resolverClass.isRelevantForCurrentInstance())

    this.resolverLookup = {}
    for (const key of tagKeys) {
      for (const resolverClass of relevantResolverClasses) {
        const resolver = new resolverClass()
        if (await resolver.doesTagKeyMatch(key, firstDataPoint as object, undefined)) {
          this.resolverLookup[key] = resolver
          break
        }
      }
    }

    await Promise.all(Object.values(this.resolverLookup).map(resolver => resolver.prepareResolver()))
  }

  async prepareData(
    entries: T[],
    timestampProperty: string,
    groupByDimensions: string[] = [],
    compiledTimeSetup: CompiledChartTimeSetup
  ): Promise<Highcharts.SeriesOptionsType[]> {
    if (entries.length === 0) return []
    await this.prepareResolvers(groupByDimensions, entries[0])
    const series = this.groupEntriesBySeries(entries, groupByDimensions)
    const segmentBounds = this.generateSegmentBounds(compiledTimeSetup)
    return await this.transformSeriesToHighcharts(series, segmentBounds, timestampProperty)
  }

  protected groupEntriesBySeries(entries: T[], groupByDimensions: string[]): SeriesData<T>[] {
    const seriesMap = new Map<string, SeriesData<T>>()

    for (const entry of entries) {
      const seriesKey = this.generateSeriesKey(entry, groupByDimensions)

      if (!seriesMap.has(seriesKey)) {
        seriesMap.set(seriesKey, {
          dimensions: this.extractDimensions(entry, groupByDimensions),
          entries: [],
        })
      }

      seriesMap.get(seriesKey)!.entries.push(entry)
    }

    return Array.from(seriesMap.values())
  }

  protected generateSeriesKey(entry: T, dimensions: string[]): string {
    return dimensions.map(dim => String(_.get(entry, dim, ''))).join('|')
  }

  protected extractDimensions(entry: T, dimensions: string[]): SeriesDimensions {
    return dimensions.reduce<SeriesDimensions>((acc, dim) => {
      acc[dim] = _.get(entry, dim)
      return acc
    }, {})
  }

  protected generateSegmentBounds(timeSetup: CompiledChartTimeSetup): SegmentBound[] {
    const now = Date.now()
    const { start: startBound, end: endBound } = this.calculateTimeBounds(timeSetup)
    const bounds: SegmentBound[] = []

    let currentStart = DateTime.fromMillis(startBound)

    while (currentStart < endBound) {
      const { end: segmentEnd, includesDSTShift } = this.calculateSegmentEnd(currentStart, timeSetup.timeResolution)

      bounds.push(this.createSegmentBound(currentStart, segmentEnd, includesDSTShift, now))
      currentStart = segmentEnd
    }

    return bounds
  }

  protected calculateTimeBounds(timeSetup: CompiledChartTimeSetup): {
    start: number
    end: DateTime
  } {
    const start = DateTime.fromMillis(timeSetup.start).startOf(this.DAY_START)
    let end = DateTime.fromMillis(timeSetup.end).endOf(this.DAY_START)

    if (end.offset !== DateTime.fromMillis(timeSetup.end).offset) {
      const offsetDiff = end.offset - DateTime.fromMillis(timeSetup.end).offset
      end = end.plus({ [this.MINUTES_UNIT]: offsetDiff })
    }

    return { start: start.toMillis(), end }
  }

  protected calculateSegmentEnd(
    start: DateTime,
    timeResolution: number
  ): {
    end: DateTime
    includesDSTShift: boolean
  } {
    let end = start.plus({ [this.MILLISECOND_UNIT]: timeResolution })
    let includesDSTShift = false

    if (start.offset !== end.offset) {
      const offsetDiff = start.offset - end.offset
      end = end.plus({ [this.MINUTES_UNIT]: offsetDiff })
      includesDSTShift = true
    }

    return { end, includesDSTShift }
  }

  protected createSegmentBound(start: DateTime, end: DateTime, includesDSTShift: boolean, now: number): SegmentBound {
    const startMillis = start.toMillis()
    const endMillis = end.toMillis()

    return {
      start: startMillis,
      end: endMillis,
      includesDSTShift,
      actualLength: end.diff(start, this.HOURS_UNIT).hours,
      alreadyDone: endMillis < now,
      notYetStarted: startMillis > now,
    }
  }

  protected async transformSeriesToHighcharts(
    series: SeriesData<T>[],
    segmentBounds: SegmentBound[],
    timestampProperty: string
  ): Promise<Highcharts.SeriesOptionsType[]> {
    const highChartsSeries: unknown[] = []

    for (const seriesElement of series) {
      const data = this.generateSeriesData(seriesElement.entries, segmentBounds, timestampProperty)

      highChartsSeries.push({
        ...seriesElement,
        data,
        name: await this.buildSeriesName(seriesElement.dimensions),
      })
    }

    return highChartsSeries as Highcharts.SeriesOptionsType[]
  }

  protected generateSeriesData(entries: T[], segmentBounds: SegmentBound[], timestampProperty: string): DataPoint[] {
    const entrySet = new Set(entries)
    const processedEntries = this.createProcessedEntriesTracker()

    return segmentBounds.map(bound => {
      const dataPoint = this.createBaseDataPoint(bound)

      for (const entry of entrySet) {
        if (this.isEntryProcessed(processedEntries, entry)) continue

        if (this.shouldIncludeEntry(entry, bound, timestampProperty)) {
          dataPoint.y++
          this.markEntryAsProcessed(processedEntries, entry)
        }
      }

      return dataPoint
    })
  }

  /**
   * Create a tracker for processed entries. Override for custom tracking logic.
   */
  protected createProcessedEntriesTracker(): Set<T> {
    return new Set<T>()
  }

  /**
   * Check if an entry has been processed. Override for custom logic.
   */
  protected isEntryProcessed(tracker: Set<T>, entry: T): boolean {
    return tracker.has(entry)
  }

  /**
   * Mark an entry as processed. Override for custom logic.
   */
  protected markEntryAsProcessed(tracker: Set<T>, entry: T): void {
    tracker.add(entry)
  }

  protected createBaseDataPoint(bound: SegmentBound): DataPoint {
    return {
      start: bound.start,
      end: bound.end,
      x: bound.start,
      y: 0,
    }
  }

  protected isTimestampInBounds(timestamp: number, bound: SegmentBound): boolean {
    return bound.start <= timestamp && timestamp < bound.end
  }

  protected async buildSeriesName(dimensions: SeriesDimensions): Promise<string> {
    let useTotalName = true
    const humanizedTagValues: string[] = []

    for (const key in dimensions) {
      useTotalName = false
      if (this.resolverLookup[key]) {
        humanizedTagValues.push(await this.resolverLookup[key].humanizeTagValue(dimensions[key]))
      } else {
        humanizedTagValues.push(String(dimensions[key]))
      }
    }

    return useTotalName ? 'Total' : humanizedTagValues.join(', ')
  }
}
